import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import { BrandMark } from '../components/BrandMark'

// ── types ─────────────────────────────────────────────────────────────────────

type Step = 'email' | 'login' | 'signup'

interface GoogleCredentialResponse {
  credential: string
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: object) => void
          renderButton: (el: HTMLElement, config: object) => void
          prompt: () => void
        }
      }
    }
    handleGoogleCredential?: (response: GoogleCredentialResponse) => void
  }
}

// ── api helpers ───────────────────────────────────────────────────────────────

const API = {
  checkEmail: async (email: string): Promise<{ exists: boolean; has_password?: boolean; has_google?: boolean }> => {
    const res = await fetch('/api/auth/check-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email }),
    })
    if (!res.ok) throw new Error('server')
    return res.json()
  },

  login: async (email: string, password: string): Promise<void> => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'loginFailed')
  },

  register: async (email: string, password: string): Promise<void> => {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'registerFailed')
  },

  googleLogin: async (credential: string): Promise<void> => {
    const res = await fetch('/api/auth/google', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ credential }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'googleFailed')
  },
}

// ── component ─────────────────────────────────────────────────────────────────

export default function LoginPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleReady, setGoogleReady] = useState(false)

  const googleBtnRef = useRef<HTMLDivElement>(null)

  // Resolve a thrown error into display text: known sentinel codes map to
  // translated strings; any other message (e.g. a backend error sentence) is
  // shown as-is.
  const errorText = (err: unknown, fallbackKey: string): string => {
    const code = err instanceof Error ? err.message : ''
    const known = ['server', 'loginFailed', 'registerFailed', 'googleFailed', 'generic', 'connection']
    if (known.includes(code)) return t(`login.errors.${code}`)
    if (code) return code
    return t(`login.errors.${fallbackKey}`)
  }

  // ── Google Identity Services init ──────────────────────────────────────────

  useEffect(() => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    if (!clientId) return  // not configured yet — button stays hidden

    window.handleGoogleCredential = async (response: GoogleCredentialResponse) => {
      setLoading(true)
      setError('')
      try {
        await API.googleLogin(response.credential)
        navigate('/exams', { replace: true })
      } catch (err) {
        setError(errorText(err, 'googleFailed'))
      } finally {
        setLoading(false)
      }
    }

    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = () => {
      if (!window.google) return
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: window.handleGoogleCredential,
        auto_select: false,
        cancel_on_tap_outside: true,
      })
      setGoogleReady(true)
    }
    document.head.appendChild(script)

    return () => {
      delete window.handleGoogleCredential
    }
  }, [navigate])

  // Render Google button when ref + SDK are both ready
  useEffect(() => {
    if (!googleReady || !googleBtnRef.current || !window.google) return
    window.google.accounts.id.renderButton(googleBtnRef.current, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      shape: 'rectangular',
      width: googleBtnRef.current.offsetWidth || 400,
    })
  }, [googleReady, step])

  // ── handlers ──────────────────────────────────────────────────────────────

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    setLoading(true)
    setError('')
    try {
      const { exists } = await API.checkEmail(email.trim().toLowerCase())
      setStep(exists ? 'login' : 'signup')
    } catch {
      setError(t('login.errors.connection'))
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      if (step === 'login') {
        await API.login(email, password)
      } else {
        await API.register(email, password)
      }
      navigate('/exams', { replace: true })
    } catch (err) {
      setError(errorText(err, 'generic'))
    } finally {
      setLoading(false)
    }
  }

  const goBack = () => {
    setStep('email')
    setPassword('')
    setError('')
  }

  // ── render ────────────────────────────────────────────────────────────────

  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID

  return (
    <div className="auth-page">
      <div className="auth-lang-switch">
        <LanguageSwitcher variant="compact" />
      </div>
      <div className="auth-card card">
        {/* Logo / wordmark */}
        <div className="auth-header">
          <div className="auth-logo">
            <BrandMark size={56} alt="" />
          </div>
          <h1 className="auth-title">{t('appName')}</h1>
          {step === 'email' && (
            <p className="auth-subtitle">{t('login.subtitle')}</p>
          )}
          {step === 'login' && (
            <p className="auth-subtitle">{t('login.welcomeBack')}</p>
          )}
          {step === 'signup' && (
            <>
              <p className="auth-subtitle-bold">{t('login.newHere')}</p>
              <p className="auth-subtitle">{t('login.setPassword')}</p>
            </>
          )}
        </div>

        {/* Step: email */}
        {step === 'email' && (
          <>
            {/* Google button */}
            {clientId ? (
              <div className="auth-google-wrapper">
                <div ref={googleBtnRef} className="auth-google-btn-container" />
              </div>
            ) : (
              <div className="auth-google-stub" title={t('login.googleNotConfigured')}>
                <svg className="auth-google-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                {t('login.continueWithGoogle')}
              </div>
            )}

            <div className="auth-divider">
              <span>{t('login.or')}</span>
            </div>

            <form onSubmit={handleEmailSubmit} className="auth-form">
              <div className="form-group">
                <label className="form-label">{t('login.email')}</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder={t('login.emailPlaceholder')}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoFocus
                  autoComplete="email"
                  required
                  disabled={loading}
                />
              </div>
              {error && <p className="auth-error">{error}</p>}
              <button
                type="submit"
                className="btn btn-primary auth-submit-btn"
                disabled={loading || !email.trim()}
              >
                {loading ? t('login.checking') : t('login.continue')}
              </button>
            </form>
          </>
        )}

        {/* Step: login or signup */}
        {(step === 'login' || step === 'signup') && (
          <form onSubmit={handlePasswordSubmit} className="auth-form">
            {/* Email display (read-only) */}
            <div className="auth-email-pill">
              <span className="auth-email-pill-text">{email}</span>
              <button
                type="button"
                className="auth-email-pill-change"
                onClick={goBack}
              >
                {t('login.change')}
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">
                {step === 'signup' ? t('login.createPasswordLabel') : t('login.passwordLabel')}
              </label>
              <input
                type="password"
                className="form-input"
                placeholder={step === 'signup' ? t('login.createPasswordPlaceholder') : t('login.passwordPlaceholder')}
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoFocus
                autoComplete={step === 'signup' ? 'new-password' : 'current-password'}
                required
                minLength={step === 'signup' ? 8 : 1}
                disabled={loading}
              />
            </div>

            {error && <p className="auth-error">{error}</p>}

            <button
              type="submit"
              className="btn btn-primary auth-submit-btn"
              disabled={loading || !password}
            >
              {loading
                ? (step === 'signup' ? t('login.creatingAccount') : t('login.loggingIn'))
                : (step === 'signup' ? t('login.createAccount') : t('login.logIn'))}
            </button>
          </form>
        )}

        {/* Footer */}
        <p className="auth-footer">
          {t('login.footerPrefix')}{' '}
          <a href="#" className="auth-link">{t('login.terms')}</a>{t('login.footerAnd')}{' '}
          <a href="#" className="auth-link">{t('login.privacy')}</a>{t('login.footerSuffix')}
        </p>
      </div>
    </div>
  )
}
